// Gemeinsame Karten-Vorlage der Lese-Ansicht: eine Kapitel-Karte mit Titelbild und
// Überblick, danach pro Artikel eine Karte je Abschnitt (Einleitung, Science, Risks,
// Learning, Ende). Long- und Short-Kapitel teilen sich diese Vorlage, damit Layout und
// Audio-Anker nicht auseinanderlaufen.
import { useState, type ReactNode } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import 'katex/dist/katex.min.css'
import { rehypeCitations } from '@/shared/lib/rehypeCitations'
import { markdownLinkComponents, markdownUrlTransform } from '@/shared/lib/articleLinks'
import { Accordion } from '@/shared/ui/accordion'
import {
  isSectionHiddenByPrefs,
  NO_READING_PREFS,
  SECTION_DISPLAY_LABELS,
  type ReadingPrefs,
} from '@/shared/lib/sectionTopics'
import { sectionUnitKey } from '@/shared/lib/sectionUnitKey'
import {
  isImageSection,
  isMediaSection,
  isVideoSection,
  parseSectionImages,
  parseSectionVideo,
} from '@/shared/lib/articleSections'
import { ArticleImageSlider } from './ArticleImageSlider'
import { ArticleVideo } from './ArticleVideo'
import { fallbackToOriginalOnError, getChapterImageUrl } from '@/shared/lib/imageUtils'
import { formatDurationMinSec } from '@/shared/lib/formatDuration'
import type { Article } from './types'

// Der Abstand kommt von außen: nach der Kopfkarte etwas mehr Luft (mt-4), zwischen den
// Inhaltskarten weniger (mt-2).
const CARD_CLASS =
  'bg-white dark:bg-[hsl(17,18%,27%)] border border-border dark:border-transparent rounded-2xl px-6 py-8 prose dark:prose-invert max-w-none prose-headings:text-foreground dark:prose-headings:text-white prose-p:text-foreground dark:prose-p:text-white text-foreground dark:text-white scroll-mt-16'

const EYEBROW_CLASS =
  'not-prose text-[13px] font-semibold text-primary/60 uppercase tracking-wide mb-3'

/** Dezenter Titel-Zusatz: kleine Versalien direkt über dem Haupttitel. */
const SUBTITLE_CLASS =
  'not-prose text-[11px] font-medium text-primary/50 dark:text-white/70 uppercase tracking-wide mb-1'

function TitleSubtitle({ subtitle }: { subtitle?: string | null }) {
  if (!subtitle) return null
  return <p className={SUBTITLE_CLASS}>{subtitle}</p>
}

// Abschnitts-Karten stehen enger: kleinerer Abstand unter dem Eyebrow, und der erste
// Absatz des Markdowns verliert seinen prose-Vorabstand.
const SECTION_EYEBROW_CLASS =
  'not-prose text-[13px] font-semibold text-primary/60 uppercase tracking-wide mb-1'
const TIGHT_MARKDOWN_CLASS = '[&>*:first-child]:mt-0'

// Liegt jetzt in @/lib/articleLinks (kennt neben externen Links auch die
// Artikel-Querverweise). Re-Export, damit bestehende Importpfade gültig bleiben.
export { markdownLinkComponents }

/** Nackte URLs in den Quellen klickbar machen. */
function autoLinkDOI(text: string): string {
  return text.replace(/(https?:\/\/[^\s]+)/g, (url) => `[${url}](${url})`)
}

/** Quellen stehen unter der letzten Karte, nicht darin - zurückhaltend und aufklappbar. */
function SourcesAccordion({ sources }: { sources: string }) {
  return (
    <div className="mt-4 text-foreground/70 dark:text-white/60">
      <Accordion title="Quellen" subtle>
        <ReactMarkdown urlTransform={markdownUrlTransform} components={markdownLinkComponents()}>
          {autoLinkDOI(sources)}
        </ReactMarkdown>
      </Accordion>
    </div>
  )
}

/** Aufklappbarer Block im Kopf ("Worum geht's", "Auf einen Blick"). */
function CollapsibleBlock({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-t border-border dark:border-white/10 pt-3">
      {/* Schriftbild wie die Überschriften in der Artikel-Übersicht - keine Versalien. */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full gap-1.5 text-md text-foreground dark:text-white"
      >
        <span>{label}</span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-primary/60 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  )
}

/**
 * Spalte der Lese-Ansicht. Short- und Long-Kapitel teilen sie sich, damit Breite,
 * Innenabstand und damit auch das Scrollverhalten identisch bleiben. Der Scroll-Container
 * (`flex-1 overflow-y-auto pt-14 pb-48`) liegt jeweils im Overlay darum herum.
 */
export function ReadingCards({ children }: { children: React.ReactNode }) {
  return <div className="w-full max-w-3xl mx-auto px-4">{children}</div>
}

/**
 * Schlusspunkt unter einem Artikel. Es gibt genau zwei Wege, einen Artikel zu beenden:
 * diesen Knopf oder die letzte Audiodatei bis zum Ende hören. Durchscrollen zaehlt
 * bewusst nicht - am Ende angekommen zu sein heisst nicht, gelesen zu haben.
 */
export function ArticleCompleteButton({
  done,
  onComplete,
}: {
  done: boolean
  onComplete: () => void
}) {
  return (
    <div className="mt-4 flex justify-center">
      {done ? (
        <span className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-primary/70 dark:text-white/60">
          <Check className="w-4 h-4 shrink-0" />
          Beendet
        </span>
      ) : (
        <button
          type="button"
          onClick={onComplete}
          className="inline-flex items-center gap-2 bg-primary text-background dark:text-white font-semibold text-sm px-10 py-3 rounded-xl hover:opacity-80 active:opacity-70 transition touch-manipulation select-none"
        >
          Beenden
        </button>
      )}
    </div>
  )
}

type ChapterIntroCardProps = {
  title: string
  subtitle?: string | null
  imageUrl?: string | null
  imageAlt?: string | null
  hook?: string | null
  summary?: string | null
  takeaway?: string | null
  articleCount?: number
  duration?: number | null
}

export function ChapterIntroCard({
  title,
  subtitle,
  imageUrl,
  imageAlt,
  hook,
  summary,
  takeaway,
  articleCount = 0,
  duration,
}: ChapterIntroCardProps) {
  return (
    <div className="bg-white dark:bg-[hsl(17,18%,27%)] border border-border dark:border-transparent rounded-2xl overflow-hidden">
      {imageUrl && (
        <div className="relative">
          <img
            src={imageUrl}
            alt={imageAlt || title}
            className="w-full object-cover"
            style={{ maxHeight: '280px' }}
            decoding="async"
            onError={fallbackToOriginalOnError}
          />
        </div>
      )}
      <div className="p-6 space-y-4">
        <div>
          <TitleSubtitle subtitle={subtitle} />
          <h1
            className="text-xl text-primary dark:text-white"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            {title}
          </h1>
        </div>
        {hook && (
          <p className="text-sm text-foreground/80 dark:text-white/80 leading-relaxed">{hook}</p>
        )}
        {(articleCount > 0 || !!duration) && (
          <p className="text-xs text-foreground/50 dark:text-white/75">
            {articleCount > 0 && `${articleCount} Artikel`}
            {articleCount > 0 && !!duration && ' • '}
            {!!duration && formatDurationMinSec(duration)}
          </p>
        )}
        <OverviewBody summary={summary} takeaway={takeaway} />
      </div>
    </div>
  )
}

/** "Worum geht's" und "Auf einen Blick" - identisch in Kapitel- und Artikel-Header. */
function OverviewBody({
  summary,
  takeaway,
}: {
  summary?: string | null
  takeaway?: string | null
}) {
  return (
    <>
      {summary && (
        <CollapsibleBlock label="Worum geht's?">
          <p className="text-sm text-foreground/80 dark:text-white/80 leading-relaxed">{summary}</p>
        </CollapsibleBlock>
      )}
      {takeaway && (
        <CollapsibleBlock label="Auf einen Blick">
          <div className="prose prose-sm max-w-none dark:prose-invert text-sm text-foreground/80 dark:text-white/80 leading-relaxed">
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex, rehypeCitations]}
              urlTransform={markdownUrlTransform}
              components={markdownLinkComponents()}
            >
              {takeaway}
            </ReactMarkdown>
          </div>
        </CollapsibleBlock>
      )}
    </>
  )
}

/**
 * Kopfkarte eines Artikels - baugleich mit der Kapitel-Karte des Short-Kapitels:
 * Titelbild, Titel, Hook, "Worum geht's", "Auf einen Blick".
 *
 * Sie ist das Sprungziel der Artikel-Buttons aus der Übersicht - der Audioplayer kennt
 * sie dagegen nicht und springt weiterhin auf die Einleitungs-Karte.
 */
function ArticleHeaderCard({
  article,
  cardRef,
  imageAction,
}: {
  article: Article
  cardRef?: (el: HTMLDivElement | null) => void
  /** Oben rechts auf dem Titelbild (der Merken-Knopf) - ohne Bild gibt es keinen Platz dafür. */
  imageAction?: ReactNode
}) {
  return (
    <div
      ref={cardRef}
      className="mt-4 first:mt-0 bg-white dark:bg-[hsl(17,18%,27%)] border border-border dark:border-transparent rounded-2xl overflow-hidden scroll-mt-16"
    >
      {article.image_id && (
        <div className="relative">
          <img
            src={getChapterImageUrl(article.image_id)}
            alt={article.image_alt || article.title}
            className="w-full object-cover"
            style={{ maxHeight: '280px' }}
            decoding="async"
            onError={fallbackToOriginalOnError}
          />
          {imageAction}
        </div>
      )}
      <div className="p-6 space-y-4">
        <div>
          <TitleSubtitle subtitle={article.subtitle} />
          <h2
            className="text-xl text-primary dark:text-white"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            {article.title}
          </h2>
        </div>
        {article.hook && (
          <p className="text-sm text-foreground/80 dark:text-white/80 leading-relaxed">
            {article.hook}
          </p>
        )}
        <OverviewBody summary={article.summary} takeaway={article.takeaway} />
      </div>
    </div>
  )
}

/** Artikeltitel in der hervorgehobenen Schrift, oben in der ersten Inhaltskarte. */
function ArticleTitle({ title, subtitle }: { title: string; subtitle?: string | null }) {
  return (
    <div className="mb-4">
      <TitleSubtitle subtitle={subtitle} />
      <h2
        className="not-prose text-xl text-primary dark:text-white"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        {title}
      </h2>
    </div>
  )
}

type ArticleCardsProps = {
  article: Article
  /**
   * Meldet jede Karte unter ihrem Audio-Unit-Key an (Artikel-Slug für den Haupttext,
   * `sectionUnitKey` je Abschnitt). Der Player scrollt darüber zur gerade vorgelesenen Karte.
   */
  registerRef?: (unitKey: string, el: HTMLDivElement | null) => void
  /** Kopfkarte mit Hook/Überblick vor die Einleitung setzen (Lese-Ansicht der Long-Kapitel). */
  showArticleHeader?: boolean
  /**
   * Meldet die Kopfkarte unter dem Artikel-Slug an - bewusst getrennt von `registerRef`,
   * damit die Navigation dorthin springt, der Audioplayer aber auf die Einleitung.
   */
  registerHeaderRef?: (slug: string, el: HTMLDivElement | null) => void
  /** Blendet Einleitung-/Ende-Abschnitte nach den persönlichen Leseeinstellungen aus. */
  readingPrefs?: ReadingPrefs
  /**
   * Oben rechts auf dem Titelbild der Kopfkarte (der Merken-Knopf). Wird nur mit
   * Kopfkarte und Titelbild gezeigt.
   */
  headerImageAction?: ReactNode
}

// Hook, Zusammenfassung und Take-Away stehen in der Kopfkarte, der Titel oben in der
// ersten Inhaltskarte - die weiteren Abschnitts-Karten zeigen nur Eyebrow und Inhalt.
export function ArticleCards({
  article,
  registerRef,
  showArticleHeader = false,
  registerHeaderRef,
  readingPrefs = NO_READING_PREFS,
  headerImageAction,
}: ArticleCardsProps) {
  // Der Haupttext läuft nie unter die Einleitung/Ende-Präferenz (siehe Kommentar an
  // `isSectionHiddenByPrefs`) - nur eigenständige Abschnitte mit diesem Topic.
  const allSections = (article.sections || []).filter(
    (s) => !isSectionHiddenByPrefs(s.topic, readingPrefs)
  )
  // Bild- und Video-Abschnitte tragen weder Titel noch Eyebrow oder Quellen - die
  // "erste"/"letzte" Karte bestimmen deshalb nur die Themen-Abschnitte.
  const topicSections = allSections.filter((s) => !isMediaSection(s))
  // Der Haupttext ist die "Einleitung". Fehlt er (reiner Abschnitts-Artikel), übernimmt
  // der erste Abschnitt Titel und Anker, damit beides nicht doppelt erscheint. Audio allein
  // reicht nicht - ohne Text bliebe die Karte leer bis auf das Eyebrow-Label.
  const hasMainContent = !!article.content_markdown?.trim()
  const firstTopicSection = !hasMainContent ? topicSections[0] : undefined
  // Ohne Bild, Hook und Überblick wäre die Kopfkarte leer - dann bleibt sie weg und die
  // Navigation fällt auf die Einleitungs-Karte zurück.
  const hasArticleHeader =
    showArticleHeader && !!(article.image_id || article.hook || article.summary || article.takeaway)
  // Fällt die Kopfkarte weg, rückt der Titel in die erste Inhaltskarte nach. In Short-Kapiteln
  // steht er aber schon in der Kapitel-Karte darüber - dort bliebe er doppelt.
  const showInlineTitle = showArticleHeader && !hasArticleHeader

  return (
    <>
      {hasArticleHeader && (
        <ArticleHeaderCard
          article={article}
          cardRef={(el) => registerHeaderRef?.(article.slug, el)}
          imageAction={headerImageAction}
        />
      )}

      {hasMainContent && (
        <div
          ref={(el) => registerRef?.(article.slug, el)}
          data-slug={article.slug}
          className={`mt-4 first:mt-0 ${CARD_CLASS}`}
        >
          <p className={EYEBROW_CLASS}>{SECTION_DISPLAY_LABELS.einleitung}</p>
          {/* Mit Kopfkarte steht der Titel bereits dort - hier wäre er doppelt. */}
          {showInlineTitle && <ArticleTitle title={article.title} subtitle={article.subtitle} />}
          {article.content_markdown && (
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex, rehypeCitations]}
              urlTransform={markdownUrlTransform}
              components={markdownLinkComponents()}
            >
              {article.content_markdown}
            </ReactMarkdown>
          )}
        </div>
      )}

      {allSections.map((section) => {
        if (isImageSection(section)) {
          return (
            <ArticleImageSlider
              key={section.id}
              images={parseSectionImages(section.images)}
              alt={article.title}
              className="mt-2"
            />
          )
        }

        if (isVideoSection(section)) {
          return (
            <ArticleVideo
              key={section.id}
              video={parseSectionVideo(section.images)}
              className="mt-2"
            />
          )
        }

        // Ohne Haupttext hängt der Artikel-Anker (Slug-Key und data-slug für den
        // Sichtbarkeits-Tracker) an der ersten Abschnitts-Karte.
        const isArticleAnchor = section.id === firstTopicSection?.id

        return (
          <div
            key={section.id}
            ref={(el) => {
              registerRef?.(sectionUnitKey(article.slug, section.id), el)
              if (isArticleAnchor) registerRef?.(article.slug, el)
            }}
            data-slug={isArticleAnchor ? article.slug : undefined}
            className={`${isArticleAnchor ? 'mt-4' : 'mt-2'} ${CARD_CLASS}`}
          >
            <p className={SECTION_EYEBROW_CLASS}>
              {SECTION_DISPLAY_LABELS[section.topic ?? ''] || section.topic}
            </p>
            {isArticleAnchor && showInlineTitle && (
              <ArticleTitle title={article.title} subtitle={article.subtitle} />
            )}
            <div className={TIGHT_MARKDOWN_CLASS}>
              <ReactMarkdown
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex, rehypeCitations]}
                urlTransform={markdownUrlTransform}
                components={markdownLinkComponents()}
              >
                {section.content_markdown ?? ''}
              </ReactMarkdown>
            </div>
          </div>
        )
      })}

      {article.sources && <SourcesAccordion sources={article.sources} />}
    </>
  )
}
